export interface TreeNode {
    label: string;
    path: string;
    children?: TreeNode[];
    isLeaf?: boolean;
}

/**
 * Parses translation keys into a hierarchical tree structure
 * @param keys - Array of translation keys (e.g., ['app.workout.instructions.heading'])
 * @returns Tree structure for rendering
 */
export function buildTranslationTree(keys: string[]): TreeNode[] {
    const tree: Record<string, any> = {};

    // Build the tree structure
    keys.forEach((key) => {
        const parts = key.split('.');
        let current = tree;

        parts.forEach((part, index) => {
            if (!current[part]) {
                current[part] = {};
            }

            // Mark if this is a leaf node (last part of the key)
            if (index === parts.length - 1) {
                current[part].__isLeaf = true;
            }

            current = current[part];
        });
    });

    // Convert tree object to TreeNode array
    function convertToTreeNodes(obj: any, parentPath: string = ''): TreeNode[] {
        return Object.keys(obj)
            .filter((key) => key !== '__isLeaf')
            .sort()
            .map((key) => {
                const currentPath = parentPath ? `${parentPath}.${key}` : key;
                const node: TreeNode = {
                    label: key,
                    path: currentPath,
                    isLeaf: obj[key].__isLeaf || false,
                };

                const childKeys = Object.keys(obj[key]).filter((k) => k !== '__isLeaf');
                if (childKeys.length > 0) {
                    node.children = convertToTreeNodes(obj[key], currentPath);
                }

                return node;
            });
    }

    return convertToTreeNodes(tree);
}

/**
 * Filters translation keys based on selected path
 * @param keys - All translation keys
 * @param selectedPath - Selected path from tree (e.g., 'app.workout')
 * @returns Filtered keys that match the selected path
 */
export function filterKeysByPath(keys: string[], selectedPath: string | null): string[] {
    if (!selectedPath) {
        return keys;
    }

    return keys.filter((key) => key.startsWith(selectedPath));
}
